import { CheckCircle } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
  useColorScheme
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../hooks/useScreenSize";
import {
  actionBarButtonPrimaryDarkStyle,
  actionBarButtonPrimaryLightStyle
} from "../../styles";
import { LegalType } from "../../types";
import { TermsAndConditionsPopup } from "../Popups/TermsAndConditionsPopup";

interface SubmitAvailabilityDialogProps {
  open: boolean;
  handleClose: () => void;
  handleSave: (text: string) => void;
  saving: boolean;
  legal: LegalType[];
  selectedLanguages: Record<string, string>;
  onSelectedLanguagesChange: (next: Record<string, string>) => void;
}

const SubmitAvailabilityDialog: React.FC<SubmitAvailabilityDialogProps> = ({
  open,
  handleClose,
  handleSave,
  saving,
  legal = [],
  selectedLanguages,
  onSelectedLanguagesChange
}) => {
  const { mode: themeVariant } = useColorScheme();
  const { isSmallScreen } = useScreenSize();
  const enableTnCState = React.useMemo(() => legal.length > 0, [legal]);
  const [tnCPopupState, setTnCPopupState] = useState(false);
  const [tncAcceptedState, setTnCAcceptedState] = useState(false);
  const [comment, setComment] = useState("");
  const { t } = useTranslation();

  const handleSubmitClick = () => {
    handleSave(comment);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="submit-availability-dialog-title"
      aria-describedby="submit-availability-dialog-description"
    >
      <TermsAndConditionsPopup
        legal={legal}
        open={tnCPopupState}
        onClose={() => {
          setTnCPopupState(false);
        }}
        selectedLanguages={selectedLanguages}
        onSelectedLanguagesChange={onSelectedLanguagesChange}
      />
      <DialogTitle id="submit-availability-dialog-title">
        {t("ROOSTER.PUBLIC-APP.CONFIRM_AVAILABILITY", {
          defaultValue: "Confirm your availability"
        })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="submit-availability-dialog-description"
          fontSize={15}
        >
          {t("ROOSTER.PUBLIC-APP.AVAILABILITY_COMMENTS_INFO", {
            defaultValue:
              "Optionally you may enter any comments regarding your availability"
          })}
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="comment"
          label={t("ROOSTER.PUBLIC-APP.COMMENTS", {
            defaultValue: "Comments"
          })}
          type="text"
          fullWidth
          variant="outlined"
          value={comment}
          onChange={handleCommentChange}
        />

        <Stack direction="column" width="100%" justifyContent="flex-end">
          {enableTnCState && (
            <Stack sx={{ mx: "-10px !important", mt: "10px !important" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="termsAndConditions"
                    checked={tncAcceptedState}
                    onChange={() => {
                      setTnCAcceptedState(!tncAcceptedState);
                    }}
                  />
                }
                label={
                  <Typography component="span">
                    {t(
                      "ROOSTER.BOOKING-FORM.I-AGREE-WITH-TERMS-&-CONDITION-1",
                      {
                        defaultValue: "I agree to the"
                      }
                    )}{" "}
                    &nbsp;
                    <Link
                      component="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setTnCPopupState(true);
                      }}
                      underline="always"
                      sx={{
                        fontSize: "14px !important",
                        fontWeight: "bold",
                        color: themeVariant === "dark" ? "#90caf9" : "#1a4d7c"
                      }}
                    >
                      {t(
                        "ROOSTER.BOOKING-FORM.I-AGREE-WITH-TERMS-&-CONDITION-2",
                        {
                          defaultValue:
                            "Terms and Conditions and Privacy Policy"
                        }
                      )}
                    </Link>
                  </Typography>
                }
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          sx={{
            ...(themeVariant === "light"
              ? {
                  ...actionBarButtonPrimaryLightStyle("text"),
                  color: "#375990"
                }
              : actionBarButtonPrimaryDarkStyle("text")),
            backgroundColor: "transparent",
            minWidth: isSmallScreen ? "auto" : "64px",
            border: "0 !important"
          }}
          onClick={handleClose}
          autoFocus
          disabled={saving}
        >
          <ArrowBackIcon /> &nbsp;
          {t("ROOSTER.PUBLIC-APP.CANCEL", {
            defaultValue: "Cancel"
          })}
        </Button>
        <Button
          size="small"
          onClick={handleSubmitClick}
          color="primary"
          disabled={saving || (enableTnCState && !tncAcceptedState)}
        >
          {saving ? (
            <CircularProgress
              size={15}
              variant="indeterminate"
              thickness={6}
              sx={{ mr: "10px" }}
              aria-label={t("ROOSTER.PUBLIC-APP.SUBMITTING", {
                defaultValue: "Submitting"
              })}
            />
          ) : (
            <CheckCircle />
          )}{" "}
          &nbsp;
          {!saving
            ? t("ROOSTER.PUBLIC-APP.SUBMIT", {
                defaultValue: "Submit"
              })
            : t("ROOSTER.PUBLIC-APP.SUBMITTING", {
                defaultValue: "Submitting"
              })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitAvailabilityDialog;
